import {
  AfterViewInit,
  Component,
  computed,
  createNgModule,
  DestroyRef,
  ElementRef,
  inject,
  Injector,
  NgZone,
  OnDestroy,
  signal,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Actions, ofActionDispatched, Store } from '@ngxs/store';
import { tap } from 'rxjs/operators';
import {
  CloseAgentPanel,
  OpenAgentPanel,
} from '../../../agent-chat/store/agent.actions';
import {
  BUBBLE_SIZE_PX,
  CONTAINER_MIDPOINT_DIVISOR,
  DRAG_THRESHOLD_PX,
  EDGE_MARGIN_PX,
  SNAP_ANIMATION_DURATION_MS,
} from '../../agent-bubble.constants';

@Component({
  selector: 'app-agent-bubble',
  templateUrl: './agent-bubble.component.html',
  styleUrls: ['./agent-bubble.component.scss'],
  standalone: false,
})
export class AgentBubbleComponent implements AfterViewInit, OnDestroy {
  private readonly onMoveBound = this.onMouseMove.bind(this);
  private readonly onUpBound = this.onMouseUp.bind(this);

  private panelLoaded = false;
  private prePanelPos: { x: number; y: number } | null = null;
  private dragStart!: {
    mouseX: number;
    mouseY: number;
    bubbleX: number;
    bubbleY: number;
  };
  private hasDragged = false;
  private snapTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private popTimeoutId: ReturnType<typeof setTimeout> | null = null;

  private readonly store = inject(Store);
  private readonly injector = inject(Injector);
  private readonly zone = inject(NgZone);
  private readonly elRef = inject(ElementRef<HTMLElement>);
  private readonly destroyRef = inject(DestroyRef);

  isHovered = signal(false);
  isDragging = signal(false);
  isSnapping = signal(false);
  isRestoring = signal(false);
  isPopping = signal(false);
  isPanelOpen = signal(false);
  pos = signal<{ x: number; y: number }>({ x: 0, y: 0 });

  @ViewChild('panelHost', { read: ViewContainerRef })
  panelHost!: ViewContainerRef;

  readonly isOnRightEdge = computed(() => {
    const containerWidth =
      this.elRef?.nativeElement?.parentElement?.clientWidth ??
      window.innerWidth;
    return this.pos().x > containerWidth / CONTAINER_MIDPOINT_DIVISOR;
  });

  constructor() {
    inject(Actions)
      .pipe(
        ofActionDispatched(CloseAgentPanel),
        tap(() => this.onPanelClose()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  ngAfterViewInit(): void {
    const { width, height } = this.containerRect;
    this.pos.set({
      x: width - BUBBLE_SIZE_PX - EDGE_MARGIN_PX,
      y: height - BUBBLE_SIZE_PX - EDGE_MARGIN_PX,
    });
  }

  ngOnDestroy(): void {
    document.removeEventListener('mousemove', this.onMoveBound);
    document.removeEventListener('mouseup', this.onUpBound);

    if (this.snapTimeoutId !== null) clearTimeout(this.snapTimeoutId);
    if (this.popTimeoutId !== null) clearTimeout(this.popTimeoutId);
  }

  onMouseDown(event: MouseEvent): void {
    event.preventDefault();
    document.addEventListener('mouseup', this.onUpBound);
    if (this.isPanelOpen()) return;
    this.dragStart = {
      mouseX: event.clientX,
      mouseY: event.clientY,
      bubbleX: this.pos().x,
      bubbleY: this.pos().y,
    };
    this.hasDragged = false;
    this.isSnapping.set(false);
    document.addEventListener('mousemove', this.onMoveBound);
  }

  /**
   * WHY LAZY LOADING:
   * AgentChatModule is kept out of the main bundle because it pulls in NGXS
   * feature state, the chat panel, and all related services — none of which
   * are needed until the user deliberately opens the panel. This keeps
   * first-load performance unaffected by the agent feature.
   *
   * WHY NOT USE loadChildren / loadComponent:
   * Angular's built-in lazy loading APIs are router-only. For non-router use
   * cases like this, programmatic `createNgModule` + dynamic `import()` is
   * the Angular-sanctioned approach. @defer is the other alternative but only
   * works with standalone components, which this project does not use.
   *
   * WHY THE panelLoaded GUARD:
   * The module and component are instantiated only once. Subsequent opens
   * skip straight to dispatching `OpenAgentPanel`, preserving the panel's
   * in-memory state across open/close cycles.
   *
   * WHY DISPATCH COMES LAST:
   * `AgentState` is registered inside `AgentChatModule` and does not exist in
   * the store until `createNgModule` runs. Dispatching before that would
   * silently drop the action.
   */
  async openPanel(): Promise<void> {
    this.isPanelOpen.set(true);
    this.prePanelPos = { ...this.pos() };
    this.snapToDefaultAndPop();

    if (this.panelLoaded) {
      this.store.dispatch(new OpenAgentPanel());
      return;
    }

    this.panelLoaded = await this.initializeChatModule();
    this.store.dispatch(new OpenAgentPanel());
  }

  onMouseEnter(): void {
    if (!this.isDragging()) this.isHovered.set(true);
  }

  private get containerRect(): DOMRect {
    return this.elRef.nativeElement.parentElement!.getBoundingClientRect();
  }

  private async initializeChatModule(): Promise<boolean> {
    const [{ AgentChatModule }, { AgentChatPanelComponent }] =
      await Promise.all([
        import('../../../agent-chat/agent-chat.module'),
        import('../../../agent-chat/components/agent-chat-panel/agent-chat-panel.component'),
      ]);

    const moduleRef = createNgModule(AgentChatModule, this.injector);
    this.panelHost.createComponent(AgentChatPanelComponent, {
      ngModuleRef: moduleRef,
    });
    return true;
  }

  private onMouseMove(event: MouseEvent): void {
    const deltaX = event.clientX - this.dragStart.mouseX;
    const deltaY = event.clientY - this.dragStart.mouseY;

    if (!this.hasDragged && Math.hypot(deltaX, deltaY) > DRAG_THRESHOLD_PX) {
      this.hasDragged = true;
      this.zone.run(() => {
        this.isDragging.set(true);
        this.isHovered.set(false);
      });
    }

    if (this.hasDragged) {
      const { width, height } = this.containerRect;
      const x = Math.max(
        0,
        Math.min(width - BUBBLE_SIZE_PX, this.dragStart.bubbleX + deltaX),
      );
      const y = Math.max(
        0,
        Math.min(height - BUBBLE_SIZE_PX, this.dragStart.bubbleY + deltaY),
      );
      this.zone.run(() => this.pos.set({ x, y }));
    }
  }

  private onMouseUp(): void {
    document.removeEventListener('mousemove', this.onMoveBound);
    document.removeEventListener('mouseup', this.onUpBound);

    if (this.isPanelOpen()) {
      this.zone.run(() => this.store.dispatch(new CloseAgentPanel()));
      return;
    }

    if (this.hasDragged) {
      this.zone.run(() => {
        this.isDragging.set(false);
        this.isSnapping.set(true);
        this.snapToEdge();
        this.snapTimeoutId = setTimeout(
          () => this.isSnapping.set(false),
          SNAP_ANIMATION_DURATION_MS,
        );
      });
    } else {
      this.zone.run(() => this.openPanel());
    }
  }

  private snapToEdge(): void {
    const { x, y } = this.pos();
    const { width, height } = this.containerRect;

    const distLeft = x;
    const distRight = width - BUBBLE_SIZE_PX - x;
    const distTop = y;
    const distBottom = height - BUBBLE_SIZE_PX - y;
    const minDist = Math.min(distLeft, distRight, distTop, distBottom);

    const clampX = (value: number) =>
      Math.max(
        EDGE_MARGIN_PX,
        Math.min(width - BUBBLE_SIZE_PX - EDGE_MARGIN_PX, value),
      );
    const clampY = (value: number) =>
      Math.max(
        EDGE_MARGIN_PX,
        Math.min(height - BUBBLE_SIZE_PX - EDGE_MARGIN_PX, value),
      );

    if (minDist === distLeft) this.pos.set({ x: EDGE_MARGIN_PX, y: clampY(y) });
    else if (minDist === distRight)
      this.pos.set({
        x: width - BUBBLE_SIZE_PX - EDGE_MARGIN_PX,
        y: clampY(y),
      });
    else if (minDist === distTop)
      this.pos.set({ x: clampX(x), y: EDGE_MARGIN_PX });
    else
      this.pos.set({
        x: clampX(x),
        y: height - BUBBLE_SIZE_PX - EDGE_MARGIN_PX,
      });
  }

  private onPanelClose(): void {
    this.isPanelOpen.set(false);
    if (!this.prePanelPos) return;

    const restoredPos = this.prePanelPos;
    this.prePanelPos = null;

    const { width, height } = this.containerRect;
    const defaultX = width - BUBBLE_SIZE_PX - EDGE_MARGIN_PX;
    const defaultY = height - BUBBLE_SIZE_PX - EDGE_MARGIN_PX;

    this.pos.set(restoredPos);

    if (restoredPos.x === defaultX && restoredPos.y === defaultY) return;

    this.isRestoring.set(true);
    this.triggerPop();
    this.snapTimeoutId = setTimeout(
      () => this.isRestoring.set(false),
      SNAP_ANIMATION_DURATION_MS,
    );
  }

  private snapToDefaultAndPop(): void {
    const { width, height } = this.containerRect;
    const defaultX = width - BUBBLE_SIZE_PX - EDGE_MARGIN_PX;
    const defaultY = height - BUBBLE_SIZE_PX - EDGE_MARGIN_PX;
    const isAlreadyAtDefault =
      this.pos().x === defaultX && this.pos().y === defaultY;

    this.pos.set({ x: defaultX, y: defaultY });

    if (!isAlreadyAtDefault) this.triggerPop();
  }

  private triggerPop(): void {
    if (this.popTimeoutId !== null) clearTimeout(this.popTimeoutId);
    this.isPopping.set(false);
    // Force reflow so the animation restarts if already popping
    this.elRef.nativeElement.getBoundingClientRect();
    this.isPopping.set(true);
    this.popTimeoutId = setTimeout(
      () => this.zone.run(() => this.isPopping.set(false)),
      SNAP_ANIMATION_DURATION_MS,
    );
  }
}
